import { ComponentPropsWithoutRef, forwardRef } from "react"
import { Button } from "@components/atoms/Button"
import { SelectedFiltersT, TaxonomyT } from "./types"
import { Chip } from "@components/atoms/Chip"
import { tv, VariantProps } from "@utils/tw"
import { TICK } from "@components/atoms/Icons"

export const ButtonSelections = ({
  taxonomy,
  type,
  style,
  selectedFilters,
  setSelectedFilters,
}: {
  taxonomy: TaxonomyT
  type: "tags" | "categories" | "documentTypes" | "documentTopics" | "focusAreas" | "sort"
  style: "button" | "chip"
  selectedFilters?: SelectedFiltersT
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFiltersT>>
}) => {
  const selected = () => {
    if (type === "tags") {
      return selectedFilters!.tags?.some((tax) => tax.slug === taxonomy.slug)
    } else if (type === "categories") {
      return selectedFilters!.categories?.some((tax) => tax.slug === taxonomy.slug)
    } else if (type === "documentTypes") {
      return selectedFilters!.documentTypes?.some((tax) => tax.slug === taxonomy.slug)
    } else if (type === "documentTopics") {
      return selectedFilters!.documentTopics?.some((tax) => tax.slug === taxonomy.slug)
    } else if (type === "focusAreas") {
      return selectedFilters!.focusAreas?.some((tax) => tax.slug === taxonomy.slug)
    }
  }

  const updateFiters = () => {
    if (!selected()) {
      switch (type) {
        case "tags":
          if (selectedFilters?.tags) {
            const tags: TaxonomyT[] = selectedFilters.tags.some((tag) => tag === taxonomy)
              ? selectedFilters.tags
              : selectedFilters.tags.concat([taxonomy])
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              tags: tags,
            }))
          }
          break
        case "categories":
          if (selectedFilters?.categories) {
            const categories: TaxonomyT[] = selectedFilters.categories.some((category) => category === taxonomy)
              ? selectedFilters.categories
              : selectedFilters.categories.concat([taxonomy])
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              categories: categories,
            }))
          }
          break
        case "documentTypes":
          if (selectedFilters?.documentTypes) {
            const documentTypes: TaxonomyT[] = selectedFilters.documentTypes.some((type) => type === taxonomy)
              ? selectedFilters.documentTypes
              : selectedFilters.documentTypes.concat([taxonomy])
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              documentTypes: documentTypes,
            }))
          }
          break
        case "documentTopics":
          if (selectedFilters?.documentTopics) {
            const documentTopics: TaxonomyT[] = selectedFilters.documentTopics.some((topic) => topic === taxonomy)
              ? selectedFilters.documentTopics
              : selectedFilters.documentTopics.concat([taxonomy])
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              documentTopics: documentTopics,
            }))
          }
          break
        case "focusAreas":
          if (selectedFilters?.focusAreas) {
            const focusAreas: TaxonomyT[] = selectedFilters.focusAreas.some((topic) => topic === taxonomy)
              ? selectedFilters.focusAreas
              : selectedFilters.focusAreas.concat([taxonomy])
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              focusAreas: focusAreas,
            }))
          }
          break
      }
    } else {
      switch (type) {
        case "tags":
          if (selectedFilters?.tags && selectedFilters.tags.includes(taxonomy)) {
            const updatedTags = selectedFilters.tags.filter((tag) => tag !== taxonomy)
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              tags: updatedTags,
            }))
          }
          break
        case "categories":
          if (selectedFilters?.categories && selectedFilters.categories.includes(taxonomy)) {
            const updatedCategories = selectedFilters.categories.filter((category) => category !== taxonomy)
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              categories: updatedCategories,
            }))
          }
          break
        case "documentTypes":
          if (selectedFilters?.documentTypes && selectedFilters.documentTypes.includes(taxonomy)) {
            const updatedDocumentTypes = selectedFilters.documentTypes.filter((type) => type !== taxonomy)
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              documentTypes: updatedDocumentTypes,
            }))
          }
          break
        case "documentTopics":
          if (selectedFilters?.documentTopics && selectedFilters.documentTopics.includes(taxonomy)) {
            const updatedDocumentTopics = selectedFilters.documentTopics.filter((topic) => topic !== taxonomy)
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              documentTopics: updatedDocumentTopics,
            }))
          }
          break
        case "focusAreas":
          if (selectedFilters?.focusAreas && selectedFilters.focusAreas.includes(taxonomy)) {
            const updatedDocumentTopics = selectedFilters.focusAreas.filter((topic) => topic !== taxonomy)
            setSelectedFilters((prevFilters) => ({
              ...prevFilters,
              focusAreas: updatedDocumentTopics,
            }))
          }
          break
      }
    }
  }

  if (style === "button")
    return (
      <FilterLiButton selected={selected()}>
        <button onClick={() => updateFiters()}>
          {TICK} <span>{taxonomy.name}</span>
        </button>
      </FilterLiButton>
    )

  if (style === "chip")
    return (
      <Chip color="black" icon="CLOSE" iconStyle="right" onClick={() => updateFiters()}>
        {taxonomy.name}
      </Chip>
    )
}

const filterLiButtonStyles = tv({
  base: [
    "[&_button]:min-h-6 [&_button]:min-w-6",
    "[&_button]:!py-3 [&_button]:!pl-3 [&_button]:!pr-[calc(24px+var(--spacing-3))]",
    "[&_button]:type-label-s [&_button]:box-border [&_button]:!leading-[1.2]",
    "[&_button]:!rounded-[6px] [&_button]:cursor-pointer [&_button]:!flex [&_button]:items-center",
    "[&_button]:justify-start [&_button]:text-left [&_button]:no-underline",
    "[&_button]:transition-colors [&_button]:ease-movement [&_button]:duration-300",
    "[&_button]:bg-light-grey [&_button]:!text-black [&_button]:!border-none",
    "[&_button]:relative [&_button]:overflow-hidden",
    "[&_button:hover]:bg-grey [&_button:hover]:!text-black",
    "[&_button:active]:bg-black [&_button:active]:!text-white",
    "[&_button:disabled]:cursor-not-allowed [&_button:disabled]:bg-grey [&_button:disabled]:!text-mid-grey",
    "md:[&_button]:max-h-7",
    "[&_button_span]:transition-transform [&_button_span]:ease-movement [&_button_span]:duration-300",
    "[&_button_svg]:absolute [&_button_svg]:top-1/2 [&_button_svg]:mt-[-12px] [&_button_svg]:left-3",
    "[&_button_svg]:size-[24px] [&_button_svg]:!translate-x-[-24px] [&_button_svg]:opacity-0",
    "[&_button_svg]:transition-[transform,opacity] [&_button_svg]:ease-movement [&_button_svg]:duration-300",
  ],
  variants: {
    selected: {
      true: [
        "[&_button]:!bg-green-mid [&_button]:!text-green",
        "[&_button:hover]:!bg-green-mid [&_button:hover]:!text-green",
        "[&_button:active]:!bg-green-mid [&_button:active]:!text-green",
        "[&_button_svg]:!text-green [&_button_svg]:!translate-x-0 [&_button_svg]:opacity-100",
        "[&_button_span]:translate-x-[30px]",
      ],
    },
  },
})

const FilterLiButton = forwardRef<
  HTMLLIElement,
  ComponentPropsWithoutRef<"li"> & VariantProps<typeof filterLiButtonStyles>
>(({ selected, className, ...props }, ref) => (
  <li ref={ref} className={filterLiButtonStyles({ selected, className })} {...props} />
))
FilterLiButton.displayName = "FilterLiButton"
